import { resolveCartSelectionIdentity } from '@/utils/cart-selection-identity';
import { createDefaultAutoOfferStore, hasExplicitAutoOfferSelection } from './index';
import type { AutoOfferStore } from './types';

/** 只保存用户意图；不保存实验、账户凭证、金额和已应用结果。 */
export type CartOfferIntent = Pick<
  AutoOfferStore,
  'couponMode' | 'pointMode' | 'coupon' | 'code' | 'expendPoint'
>;

export function getCartOfferScope(input: {
  site: string;
  authenticated: boolean;
  userId?: number | string | null;
  token?: string | null;
  tokenReady: boolean;
  userReady: boolean;
}): string | null {
  // userReady 表示账户初始化已结束，不代表资料请求成功；结束前禁止使用上个账户的缓存。
  if (!input.tokenReady || (input.authenticated && !input.userReady)) return null;
  // userId 只用于优惠缓存隔离，计价接口仍凭 token 鉴权；资料失败不能持续阻断计价。
  // 缺少 userId 时按当前 token 的摘要隔离临时选择，不保存原始凭证。
  const identity = input.authenticated
    ? input.userId
      ? `user:${input.userId}`
      : resolveCartSelectionIdentity({ authenticated: true, token: input.token })
    : 'guest';
  if (!identity) return null;
  // 沿用真实选择的存储键；不迁移已废弃的 preview 数据，避免模拟操作污染真实计价。
  return JSON.stringify([input.site, 'live', identity]);
}

function parseIntent(raw: unknown): CartOfferIntent | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const value = raw as Record<string, unknown>;
  if (!['auto', 'skip', 'manual_coupon', 'manual_code'].includes(String(value.couponMode)))
    return null;
  if (!['auto', 'none', 'manual'].includes(String(value.pointMode))) return null;
  if (
    value.couponMode === 'manual_coupon' &&
    (typeof value.coupon !== 'number' || !Number.isSafeInteger(value.coupon) || value.coupon <= 0)
  )
    return null;
  if (
    value.couponMode === 'manual_code' &&
    (typeof value.code !== 'string' || !value.code.trim() || value.code.length > 256)
  )
    return null;
  if (
    value.pointMode === 'manual' &&
    (typeof value.expendPoint !== 'number' ||
      !Number.isSafeInteger(value.expendPoint) ||
      value.expendPoint <= 0)
  )
    return null;
  return {
    couponMode: value.couponMode as CartOfferIntent['couponMode'],
    pointMode: value.pointMode as CartOfferIntent['pointMode'],
    coupon: value.couponMode === 'manual_coupon' ? (value.coupon as number) : undefined,
    code: value.couponMode === 'manual_code' ? (value.code as string).trim() : '',
    expendPoint: value.pointMode === 'manual' ? (value.expendPoint as number) : undefined,
  };
}

const storageKey = (scope: string) => `zl-cart-offer-intent:v1:${scope}`;

const guestCodeKey = (guestScope: string) => `zl-cart-offer-guest-code:v1:${guestScope}`;

// 存储被禁用时只续接当前页面；目标会话仍由同一 token 边界绑定、换号时清理。
let guestCodeInMemory: {
  guestScope: string;
  code: string | null;
  sessionScope: string | null;
} | null = null;

// 只续接同页游客登录前的主动取消；AB 重新门控会卸载组件，不能再依赖其 previous state。
// 不写入长期游客历史，不迁移积分；全局 token 边界将它限定到本次登录会话。
let guestSkipInMemory: { guestScope: string; sessionScope: string | null } | null = null;

function readGuestCode(guestScope: string) {
  // 当前页面的换码、取消和会话绑定优先，不能因存储写入失败读回旧码。
  if (guestCodeInMemory?.guestScope === guestScope)
    return guestCodeInMemory.code ? { ...guestCodeInMemory, code: guestCodeInMemory.code } : null;
  try {
    const raw = sessionStorage.getItem(guestCodeKey(guestScope));
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const record = value as Record<string, unknown>;
    const intent = parseIntent({ couponMode: 'manual_code', pointMode: 'none', code: record.code });
    if (
      record.version !== 1 ||
      record.consumed ||
      !intent ||
      (record.sessionScope !== null && typeof record.sessionScope !== 'string')
    )
      return null;
    return { code: intent.code!, sessionScope: record.sessionScope as string | null };
  } catch {
    return null;
  }
}

function clearGuestCode(guestScope: string) {
  guestCodeInMemory = { guestScope, code: null, sessionScope: null };
  try {
    // 先标记失效再删除，删除被禁用时也不能在重挂载后复活已取消或已消费的码。
    sessionStorage.setItem(
      guestCodeKey(guestScope),
      JSON.stringify({ version: 1, consumed: true }),
    );
  } catch {
    /* 配额不足时仍尝试删除。 */
  }
  try {
    sessionStorage.removeItem(guestCodeKey(guestScope));
  } catch {
    /* 当前内存或已消费标记仍能阻止回放。 */
  }
}

/** 当前标签页正在使用的游客码；不从已登录页面读取长期保存的游客历史意图。 */
export function rememberGuestCartOfferCode(guestScope: string, selection: AutoOfferStore) {
  const intent = parseIntent(selection);
  guestSkipInMemory = intent?.couponMode === 'skip' ? { guestScope, sessionScope: null } : null;
  if (intent?.couponMode !== 'manual_code') {
    // 换券或主动取消后，登录不能回放先前输入的码。
    clearGuestCode(guestScope);
    return;
  }
  guestCodeInMemory = { guestScope, code: intent.code!, sessionScope: null };
  try {
    sessionStorage.setItem(
      guestCodeKey(guestScope),
      JSON.stringify({ version: 1, code: intent.code, sessionScope: null }),
    );
  } catch {
    // 存储不可用时，当前页面仍可通过内存继承游客码。
  }
}

/** 复用全局 token 边界订阅，离开购物车后登录/换号也能限定本次迁移目标。 */
export function syncGuestCartOfferCode(site: string, previousToken: string, token: string) {
  if (previousToken === token) return;
  const guestScope = getCartOfferScope({
    site,
    authenticated: false,
    tokenReady: true,
    userReady: true,
  })!;
  try {
    if (previousToken || !token) {
      guestSkipInMemory = null;
      clearGuestCode(guestScope);
      return;
    }
    const sessionScope = getCartOfferScope({
      site,
      authenticated: true,
      token,
      tokenReady: true,
      userReady: true,
    });
    if (guestSkipInMemory?.guestScope === guestScope)
      guestSkipInMemory = { guestScope, sessionScope };
    const pending = readGuestCode(guestScope);
    if (!pending) return;
    if (pending.sessionScope && pending.sessionScope !== sessionScope) {
      clearGuestCode(guestScope);
      return;
    }
    guestCodeInMemory = { guestScope, code: pending.code, sessionScope };
    sessionStorage.setItem(
      guestCodeKey(guestScope),
      JSON.stringify({
        version: 1,
        ...pending,
        sessionScope,
      }),
    );
  } catch {
    // 同步失败不影响原有登录、勾选迁移及当前内存意图。
  }
}

/** 同页取消由页面首次恢复消费；MiniCart 预读不消费，避免其默认报价抢先覆盖取消。 */
export function readGuestCartOfferSkip(
  guestScope: string,
  sessionScope: string | null,
  consume = true,
): boolean {
  if (
    !sessionScope ||
    guestSkipInMemory?.guestScope !== guestScope ||
    (guestSkipInMemory.sessionScope && guestSkipInMemory.sessionScope !== sessionScope)
  )
    return false;
  if (consume) guestSkipInMemory = null;
  return true;
}

export function readGuestCartOfferCode(guestScope: string, sessionScope: string | null) {
  if (!sessionScope) return null;
  const pending = readGuestCode(guestScope);
  // 正常登录由全局订阅绑定；OAuth 首屏/独立挂载可能只剩尚未绑定的一次性记录。
  if (!pending || (pending.sessionScope && pending.sessionScope !== sessionScope)) return null;
  guestCodeInMemory = { guestScope, code: pending.code, sessionScope };
  try {
    sessionStorage.setItem(
      guestCodeKey(guestScope),
      JSON.stringify({
        version: 1,
        ...pending,
        sessionScope,
      }),
    );
  } catch {
    // 当前页面的内存记录也绑定目标会话，换号后不能继续继承。
  }
  return pending.code;
}

/** 账户落盘后消费；已失效/被替换的码立即作废，不等待持久化成功。 */
export function finishGuestCartOfferCode(
  guestScope: string,
  scope: string,
  sessionScope: string | null,
  selection: AutoOfferStore,
  guestIntentScope = guestScope,
) {
  const pending = readGuestCode(guestScope);
  if (!pending || pending.sessionScope !== sessionScope) return;
  const saved = readCartOfferIntent(scope);
  const stillUsingCode = selection.couponMode === 'manual_code' && selection.code === pending.code;
  // 有效码写入失败仍可续接；400200 清码、主动取消或切券后不能因缓存失败再次回放旧码。
  if (stillUsingCode && (saved?.couponMode !== 'manual_code' || saved.code !== pending.code))
    return;
  clearGuestCode(guestScope);
  try {
    // 小语种购物车意图有独立 namespace，迁移绑定仍使用真实站点的 guestScope。
    const guest = readCartOfferIntent(guestIntentScope);
    // 该码已归当前账号，只移除同码的游客记录，避免退出后再传给另一账号。
    if (guest?.couponMode === 'manual_code' && guest.code === pending.code) {
      // 删除被禁用时也先将同码历史重置，退出后不能把已归账户的码视作新的游客输入。
      writeCartOfferIntent(guestIntentScope, createDefaultAutoOfferStore());
      localStorage.removeItem(storageKey(guestIntentScope));
    }
  } catch {
    /* 清理失败不影响当前账户计价。 */
  }
}

export function readCartOfferIntent(
  scope: string,
  sessionScope?: string | null,
): CartOfferIntent | null {
  // 资料恢复后只迁移当前 token 的临时选择；写入成功才移除，防止重进时被旧账户缓存覆盖。
  const sessionIntent =
    sessionScope && sessionScope !== scope ? readCartOfferIntent(sessionScope) : null;
  if (sessionIntent) {
    // 手动券/码、主动取消或积分选择优先沿用当前会话；会话只有默认值时保留账户历史选择。
    const intent = hasExplicitAutoOfferSelection({
      ...createDefaultAutoOfferStore(),
      ...sessionIntent,
    })
      ? sessionIntent
      : readCartOfferIntent(scope) || sessionIntent;
    try {
      localStorage.setItem(storageKey(scope), JSON.stringify({ version: 1, intent }));
      localStorage.removeItem(storageKey(sessionScope!));
    } catch {
      // 存储失败不影响当前计价，也保留临时记录供后续恢复。
    }
    return intent;
  }
  try {
    const raw = localStorage.getItem(storageKey(scope));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !('version' in parsed) ||
      parsed.version !== 1 ||
      !('intent' in parsed)
    )
      return null;
    return parseIntent(parsed.intent);
  } catch {
    // 禁用存储或损坏缓存不能阻断当前页面操作。
    return null;
  }
}

export function writeCartOfferIntent(
  scope: string,
  selection: AutoOfferStore,
  sessionScope?: string | null,
): void {
  const intent = parseIntent(selection);
  if (!intent) return;
  try {
    localStorage.setItem(storageKey(scope), JSON.stringify({ version: 1, intent }));
    // 存储恢复后，新的正式选择取代未能迁移的临时记录，避免下次又恢复旧券/取消状态。
    if (sessionScope && sessionScope !== scope) localStorage.removeItem(storageKey(sessionScope));
  } catch {
    // 仅跨刷新恢复降级，不清除当前内存意图。
  }
}

/** 计价依赖只含输入，不含服务端结果，避免回写优惠结果触发请求循环。 */
export function cartOfferInputKey(selection: AutoOfferStore): string {
  return JSON.stringify([
    selection.enabled,
    selection.authenticated,
    selection.couponPolicy,
    selection.couponMode,
    selection.coupon,
    selection.code,
    selection.pointMode,
    selection.expendPoint,
  ]);
}
